import {
    DocumentElement,
    ParagraphElement,
    TextElement,
    TextAlignmentType,
    AnchorType,
    VerticalType,
    WhiteSpaceType,
    LineSpacingType,
    DocSizeType,
    AlloyNode,
} from '@tencent/alloy-editor';
import { AutoFitType, TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import {
    FONT_SCALE,
    DEFAULT_PADDING_LR, DEFAULT_PADDING_TB, DEFAULT_TAB_SIZE, TEXT_BASELINE_OFFSET,
} from '../constants';
import { createCharacterProps } from './character-transform';
import { processTextFontFamily } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/font-utils';
import { getFileIconURL } from '@tencent/docs-scenario-components-file-icon';
import { emuToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { getEffectFilter, getFillStyle, getStrokeStyle } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';

const DEFAULT_MIMETYPE = 'application-vnd.tdocs-apps.default';

const textAlignMap = {
    left: TextAlignmentType.Left,
    right: TextAlignmentType.Right,
    center: TextAlignmentType.Center,
    justify: TextAlignmentType.Justify,
    distribute: TextAlignmentType.Distribute,
};

const alignItemsMap = {
    'flex-start': AnchorType.Top,
    center: AnchorType.Center,
    'flex-end': AnchorType.Bottom,
};

const textDirectionToVerticalMap = {
    [TextDirectionType.VERTICAL]: VerticalType.Vertical,
    [TextDirectionType.ROTATE_90]: VerticalType.Vertical90,
    [TextDirectionType.ROTATE_270]: VerticalType.Vertical270,
    [TextDirectionType.WORD_ART_VERTICAL_RTL]: VerticalType.WordArtVerticalRtl,
    [TextDirectionType.WORD_ART_VERTICAL_LTR]: VerticalType.WordArtVerticalLtr,
};

const verticalToTextDirectionMap = {
    [VerticalType.Vertical]: TextDirectionType.VERTICAL,
    [VerticalType.Vertical90]: TextDirectionType.ROTATE_90,
    [VerticalType.Vertical270]: TextDirectionType.ROTATE_270,
    [VerticalType.WordArtVerticalRtl]: TextDirectionType.WORD_ART_VERTICAL_RTL,
    [VerticalType.WordArtVerticalLtr]: TextDirectionType.WORD_ART_VERTICAL_LTR,
};

/**
 * editor 中的 vertical 属性转换为 slide 中文字方向
 * @param vertical
 * @returns
 */
export function verticalToTextDirection(vertical?: VerticalType): TextDirectionType {
    if (!vertical) {
        return TextDirectionType.HORIZONTAL;
    }
    return verticalToTextDirectionMap[vertical] || TextDirectionType.HORIZONTAL;
}

const whiteSpaceMap = {
    pre: WhiteSpaceType.NowrapExceptForceBreak,
    'pre-wrap': WhiteSpaceType.Normal,
};

const lineHeightIsMap = {
    percent: LineSpacingType.Multiple,
    absolute: LineSpacingType.Exactly,
};


export default class DataTransform {
    public static toEditorValue(data, config?: { noOriginData?: boolean }): DocumentElement {
        return DataTransform.createEditorDocument(data, config?.noOriginData);
    }

    /**
     * 获取原始属性
     * @param element
     * @param filterPrefix 过滤字段前缀
     */
    public static getOriginProps(element, filterPrefix?: string) {
        const originProps = {};
        Object.entries(element).forEach(([key, value]) => {
            if (key.startsWith('origin-')) {
                const originKey = key.slice(7);
                if (filterPrefix && originKey.startsWith(filterPrefix)) {
                    return;
                }
                originProps[originKey] = value;
            }
        });
        return originProps;
    }

    /**
     * 获取原始属性
     * @param element
     * @param filterFunction 自定义过滤函数
     * @returns
     */
    public static getOriginPropsWithFilter(
        element: AlloyNode,
        filterFunction?: (key: string, value: AlloyNode[keyof AlloyNode], element: AlloyNode) => boolean,
    ): Partial<AlloyNode> {
        const originProps = {};
        Object.entries(element).forEach(([key, value]) => {
            if (key.startsWith('origin-')) {
                const originKey = key.slice(7);
                if (!filterFunction || filterFunction(originKey, value, element)) {
                    originProps[originKey] = value;
                }
                /**
                 * 如果自身属性有修改，且该字段为空的话，说明本次修改是将其设置为undefined或null
                 * 这两个属性传入editor后，editor会将其对应的key delete掉，这样会导致editor数据转到uiModel上之后没有该key
                 * 在提交数据的时候这里的更改会被忽略，给这种key单独加上一个null来确保其被删掉
                 */
                if (originKey.startsWith('raw-')) {
                    const selfOriginKey = originKey.slice(4);
                    if (element[originKey] && !element[`origin-${selfOriginKey}`]) {
                        originProps[selfOriginKey] = null;
                    }
                }
            }
        });
        return originProps;
    }

    public static transformTextStyle(key, value, styles?) {
        const fnMap = {
            fontWeight: () => ({ fontWeight: value }),
            fontSize: () => ({ fontSize: value }),
            displayFontSize: () => ({ displayFontSize: value }),
            fontFamily: () => ({ fontFamily: processTextFontFamily(value) }),
            color: () => ({ color: value }),
            backgroundColor: () => ({ background: value }),
            textDecoration: () => ({ underline: value === 'underline' }),
            del: () => ({ strike: value === 'sng' }),
            fontStyle: () => ({ italic: value === 'italic' }),
            letterSpacing: () => ({ letterSpacing: value * FONT_SCALE }),
            isSearchResult: () => ({ isSearchResult: value }),
            inlineProps: () => ({ inlineProps: value }),
            [TEXT_BASELINE_OFFSET]: () => ({ baselineOffset: value }),
            effect: () => ({ effect: getEffectFilter(value) }),
            border: () => ({ stroke: getStrokeStyle({ style: { border: value } }) }),
        };
        // 不能用 void 0，而是需要改成[key]: value是处理光标态选择效果后再输入文字
        const transformFn = fnMap[key] || (key !== 'type' ? (() => ({ [key]: value })) : (() => void 0));
        return transformFn();
    }
    public static transformParaStyle(key, value, styles?) {
        const fnMap = {
            textAlign: () => ({ textAlign: textAlignMap[value] }),
            lineHeight: () => {
                const { lineHeightIs } = styles || {};
                return { lineSpacing: lineHeightIs === 'absolute' ? Number.parseInt(value, 10) : value };
            },
            lineHeightIs: () => ({ lineSpacingRule: lineHeightIsMap[value] }),
            marginLeft: () => ({ marginLeft: value }),
            textIndent: () => ({ textIndent: value }),
            paddingBottom: () => ({ spaceAfter: value }),
            paddingTop: () => ({ spaceBefore: value }),
            tabSize: () => ({ defaultTabSize: (value ?? DEFAULT_TAB_SIZE) }),
            tabList: () => ({ tabList: value }),
        };
        const transformFn = fnMap[key] || (() => void 0);
        return transformFn();
    }

    public static transformEditorStyle(key, value, styles?) {
        const fnMap = {
            alignItems: () => ({ anchor: alignItemsMap[value] }),
            textDirection: () => ({ vertical: textDirectionToVerticalMap[value] }),
            whiteSpace: () => ({ whiteSpaceType: whiteSpaceMap[value] }),
            justifyContent: () => ({ anchorCenter: value === 'center' }),
            columnNumber: () => ({ columnNumber: value }),
            columnSpace: () => ({ columnSpace: emuToCanvas(value) }),
        };
        const transformFn = fnMap[key] || (() => void 0);
        return transformFn();
    }

    public static createParagraphElements(paragraphs, noOriginData?) {
        return paragraphs.map((paragraph, index) => (
            DataTransform.createEditorParagraph(paragraph, index, paragraphs, noOriginData)
        ));
    }

    private static getPadding(padding, defaultValue) {
        const p = typeof (padding) === 'undefined' ? defaultValue : parseFloat(padding);
        return parseInt(String(p * FONT_SCALE), 10); // 去小数
    }

    private static createEditorDocument(textBody, noOriginData?) {
        const { style, paragraphs = [] } = textBody;
        const { handingChar4OneLine = true } = style;
        const originProps = noOriginData ? { } : DataTransform.createOriginProps(style);
        let { paddingLeft, paddingTop, paddingBottom, paddingRight } = style;
        paddingLeft = this.getPadding(paddingLeft, DEFAULT_PADDING_LR);
        paddingRight = this.getPadding(paddingRight, DEFAULT_PADDING_LR);
        paddingTop = this.getPadding(paddingTop, DEFAULT_PADDING_TB);
        paddingBottom = this.getPadding(paddingBottom, DEFAULT_PADDING_TB);

        const docSizeType = this.getDocSizeType(style);
        const editorDocument: DocumentElement = {
            paddingLeft,
            paddingRight,
            paddingTop,
            paddingBottom,
            docSizeType,
            handingChar4OneLine,  // 默认进行文字探测，避免第二行出现一个字的情况
            type: 'document',
            children: this.createParagraphElements(paragraphs, noOriginData),
            ...originProps,
        };
        Object.entries(style).forEach(([key, value]) => {
            Object.assign(editorDocument, DataTransform.transformEditorStyle(key, value));
        });
        return editorDocument;
    }

    private static getDocSizeType(textBodyStyle) {
        // 根据文字调整形状大小而且文字不自动换行的话，排版出来的宽度就是文字真实的宽度
        if (textBodyStyle?.autoFitType === AutoFitType.SHAPE_AUTO_FIT && textBodyStyle.whiteSpace === 'pre') {
            return DocSizeType.WrapContent;
        }
    }

    private static createEditorParagraph(paragraph, index, paragraphs, noOriginData?) {
        const { textRuns = [], paragraphProps } = paragraph;
        const {
            marginLeft,
            textIndent = 0,
        } = paragraphProps;
        const characterProps = createCharacterProps(paragraph, index, paragraphs);
        const originProps = noOriginData ? {} : DataTransform.createOriginProps(paragraphProps);
        const editorParagraph: ParagraphElement = {
            marginLeft,
            textIndent,
            type: 'paragraph',
            characterProps,
            children: textRuns.map(textRun => DataTransform.createEditorText(textRun, noOriginData)),
            ...originProps,
        };
        Object.entries(paragraphProps).forEach(([key, value]) => {
            Object.assign(editorParagraph, DataTransform.transformParaStyle(key, value, paragraphProps));
        });
        return editorParagraph;
    }

    private static createEditorText(textRun, noOriginData?): TextElement {
        const originProps = noOriginData ? {} : DataTransform.createOriginProps(textRun.styles);
        const editorTextNode: TextElement = {
            type: 'text',
            text: `${textRun.text ?? ''}`,  // 转字符串
            ...originProps,
            stroke: getStrokeStyle({ style: { border: textRun.styles.border } }),
            color: textRun.styles.color,
            fill: getFillStyle({ fillBackground: textRun.styles }),
            // 有fieldType就认为是void
            ...(textRun.styles.fieldType || textRun.styles.href?.iconMode  ? { void: true } : {}),
        };
        if (textRun.styles.href?.iconMode) {
            editorTextNode.inlineProps = {
                ...editorTextNode.inlineProps,
                icon: getFileIconURL({ mimeType: textRun.styles.href.mimeType || DEFAULT_MIMETYPE }).target,
            };
        }
        Object.entries(textRun.styles).forEach(([key, value]) => {
            Object.assign(editorTextNode, DataTransform.transformTextStyle(key, value));
        });
        return editorTextNode;
    }

    private static createOriginProps(props) {
        const originProps = {};
        Object.entries(props).forEach(([key, value]) => {
            originProps[`origin-${key}`] = value;
        });
        return originProps;
    }
}
